/**
 * The sanitized-demo disclosure.
 *
 * The published Markdown carries this statement in every file, but the reader
 * splits a composed view into sections and renders one at a time — and a
 * section starts at its first heading, so the document preamble that holds the
 * disclaimer never reaches the screen. A visitor could read the entire report
 * and never learn the identifiers are synthetic.
 *
 * The wording tracks `cloudox/reporting/sanitize/disclaimer.py` deliberately:
 * the interface should not make a softer claim than the artifact it renders.
 */
export interface DemoNoticeProps {
  /**
   * `full` for a landing surface, `compact` for a strip above a document the
   * reader is already inside.
   */
  readonly variant?: "full" | "compact";
  readonly className?: string;
}

const SUMMARY =
  "AWS account IDs and resource identifiers have been replaced with synthetic equivalents for public safety.";
const DETAIL =
  "Architecture, workloads, findings, and relationships come from a real AWS environment.";

export function DemoNotice({ variant = "full", className = "" }: DemoNoticeProps) {
  const compact = variant === "compact";
  return (
    <aside
      className={`flex gap-2.5 rounded-lg border px-3.5 ${compact ? "py-2.5" : "py-3"} ${className}`.trim()}
      style={{
        borderColor: "color-mix(in oklab, var(--warning) 35%, transparent)",
        backgroundColor: "color-mix(in oklab, var(--warning) 8%, transparent)",
      }}
      aria-label="Demo data notice"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="mt-0.5 shrink-0"
        style={{ color: "var(--warning)" }}
        aria-hidden
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8h.01M11 12h1v4h1" />
      </svg>
      <p className={compact ? "text-xs" : "text-sm"} style={{ color: "var(--fg-muted)" }}>
        <strong style={{ color: "var(--fg)" }}>Sanitized example.</strong> {SUMMARY} {DETAIL}
      </p>
    </aside>
  );
}
